import Axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { Sentry } from '@/lib/sentry'
import { useAuthStore } from '@/stores/auth-store'
import { toast } from 'sonner'
import { resetSessionAndClearQuery } from '@/lib/session-logout'

import { extractApiErrorMessage } from '@/lib/api-error'
import { signInHrefWithRedirect } from '@/lib/auth-redirect'
import { isTransportNetworkError, toastNetworkErrorOnce } from '@/lib/network-toast'
type InternalRequestConfigWithToastControl = InternalAxiosRequestConfig & {
  _skipErrorToast?: boolean
}

import { resolveApiBaseUrl } from '@/lib/api-origin'

export const apiClient = Axios.create({
  // Endpoints in `src/config/api-endpoints.ts` are absolute paths like `/v1/me`,
  // so baseURL should be the API origin (no `/v1` suffix).
  // Prefer same-origin in the browser so reseller custom domains are not blocked by CSP.
  baseURL: resolveApiBaseUrl(),
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const authState = useAuthStore.getState().auth;
    const token = authState.getEffectiveAccessToken();
    const orgId = authState.activeOrganizationId;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (orgId) {
      config.headers['X-Organization-Id'] = orgId;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
      // Unwrap generic response wrapper if present
      if (response.data && response.data.data) {
          return response.data.data;
      }
      return response.data;
  },
  (error: AxiosError) => {
    const originalRequest = error.config as InternalRequestConfigWithToastControl & { _retry?: boolean };

    if (error.response?.status === 401) {
      const url = originalRequest?.url ?? ''
      const isUnauthenticatedFlow =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/public/signup/')
      if (isUnauthenticatedFlow) {
        return Promise.reject(error);
      }

      // Centralized session reset used by idle timeout and manual sign-out.
      resetSessionAndClearQuery()
      toast.error('Session expired. Please sign in again.');
      if (!window.location.pathname.includes('/sign-in')) {
        window.location.href = signInHrefWithRedirect(window.location.href);
      }
      return Promise.reject(error);
    }

    const status = error.response?.status
    const skipErrorToast = originalRequest?._skipErrorToast === true
    const isTransientUpstream =
      isTransportNetworkError(error) || status === 502 || status === 503 || status === 504

    if (!skipErrorToast) {
      if (isTransientUpstream) {
        // Brief API restarts / nginx upstream blips would otherwise spam every poll.
        toastNetworkErrorOnce('Connection lost. Retrying…')
      } else {
        toast.error(extractApiErrorMessage(error, 'Request failed'))
      }
    }

    // Skip errors that are expected user/auth flows — not actionable bugs.
    // 400 Bad Request  : usually a form/validation error, already surfaced in UI.
    // 401 Unauthorized : handled above (session expiry flow).
    // 403 Forbidden    : access control, expected for restricted roles.
    // Transient network/502-504: expected during deploys; still captured once via tags.
    const SKIP_STATUSES = new Set([400, 401, 403])
    const shouldCapture = !SKIP_STATUSES.has(status ?? -1)

    if (shouldCapture) {
      // 5xx, network errors (status 0), 404, 422, 429, etc.
      Sentry.withScope((scope: Sentry.Scope) => {
        scope.setTag('http.method', originalRequest?.method?.toUpperCase() ?? 'UNKNOWN')
        scope.setTag('http.url', originalRequest?.url ?? 'UNKNOWN')
        scope.setTag('http.status', String(status ?? 0))
        scope.setTag('http.transient', isTransientUpstream ? '1' : '0')
        scope.setExtra('httpStatus', status ?? 0)
        scope.setExtra('responseBody', error.response?.data)
        scope.setExtra('requestUrl', originalRequest?.url)
        scope.setExtra('navigatorOnline', typeof navigator !== 'undefined' ? navigator.onLine : null)
        Sentry.captureException(error)
      })
    }

    return Promise.reject(error)
  }
)
